import { useAsync } from "./useAsync";
import * as api from "../lib/api";
import { generatedBlogPosts } from "@/lib/blogs.generated";

/** Static blog data mapped to DB row shape — used as instant fallback for SEO. */
const STATIC_BLOG_ROWS = generatedBlogPosts.map((post, index) => ({
  id: `static-${post.slug}`,
  slug: post.slug,
  title: post.title,
  category: post.category,
  published_at: post.publishedAt,
  listing_date: post.listingDate,
  read_time: post.readTime,
  excerpt: post.excerpt,
  hero_image: post.heroImage,
  listing_image: post.listingImage,
  author_name: post.author.name,
  author_image: post.author.image,
  author_bio: post.author.bio,
  body_html: post.bodyHtml,
  is_published: true as const,
  sort_order: index,
  created_at: "",
  updated_at: "",
}));

const STATIC_BLOG_MAP = new Map(STATIC_BLOG_ROWS.map((row) => [row.slug, row]));

type BlogListOptions = {
  /** Use bundled blog content while the live CMS revalidates. */
  staticFallback?: boolean;
};

/** Fetch all published blog posts. Can render instantly with static data, then revalidate. */
export function useBlogList(options: BlogListOptions = {}) {
  const { staticFallback = true } = options;

  return useAsync(
    () => api.getBlogs(),
    [],
    staticFallback ? { initialData: STATIC_BLOG_ROWS } : undefined,
  );
}

/** Fetch a single blog post by slug. Renders instantly with static data if available. */
export function useBlogPost(slug: string | undefined) {
  return useAsync(
    () => api.getBlogBySlug(slug!),
    slug ? [slug] : null,
    {
      initialData: slug ? STATIC_BLOG_MAP.get(slug) ?? undefined : undefined,
    },
  );
}

/** Fetch all blogs including drafts (admin). No caching — always fresh. */
export function useAdminBlogs() {
  return useAsync(() => api.getAdminBlogs(), []);
}
